import classNames from 'classnames'
import DOMPurify from 'dompurify'
import { useMemo } from 'react'
import { useCountUp } from 'src/hooks/useCountUp'
import type { BigNumbersTheme } from 'src/types/bigNumbers2'
import {
  formatBigNumberValue,
  parseBigNumberValue,
} from 'src/utils/parseBigNumberValue'

interface BigNumberItemProps {
  bigNumber: string
  subtext: string
  theme: BigNumbersTheme
  shouldStartCount: boolean
}

export default function BigNumberItem({
  bigNumber,
  subtext,
  theme,
  shouldStartCount,
}: BigNumberItemProps) {
  const parsedBigNumber = useMemo(
    () => parseBigNumberValue(bigNumber),
    [bigNumber]
  )

  const animatedValue = useCountUp({
    endValue: parsedBigNumber.numericValue,
    shouldStart: shouldStartCount && parsedBigNumber.hasNumericValue,
    durationMs: 1500,
  })

  const sanitizedSubtext = useMemo(() => {
    return typeof DOMPurify?.sanitize === 'function'
      ? DOMPurify.sanitize(subtext ?? '')
      : subtext ?? ''
  }, [subtext])

  const formattedNumber = parsedBigNumber.hasNumericValue
    ? `${parsedBigNumber.prefix}${formatBigNumberValue(
        animatedValue,
        parsedBigNumber.numberToken
      )}${parsedBigNumber.suffix}`
    : parsedBigNumber.raw

  return (
    <article
      data-testid="big-number-item"
      className="flex h-full w-full max-w-[340px] flex-col items-center px-4 md:px-8 text-center"
    >
      <p
        data-testid="big-number-value"
        className={classNames(
          'inline-block min-w-[6ch] font-black text-2xl pp:text-4xl leading-tight  md:text-[62px]/tight tabular-nums',
          theme === 'dark' ? 'text-darkHighlight' : 'text-fullDark'
        )}
      >
        {formattedNumber}
      </p>

      <p
        data-testid="big-number-subtext"
        className="mt-2 text-xs font-bold uppercase tracking-[1.44px] text-graySubtitle"
        dangerouslySetInnerHTML={{
          __html: sanitizedSubtext,
        }}
      />
    </article>
  )
}
